import { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { Sortable, DragHandle, useDragSensors } from "../../shared/Sortable";
import { intro } from "../../shared/textStyles";
import { STATEMENT_KINDS, groupStatements, statementCount } from "./statementKinds";
import { useStatements } from "./useStatements";
import { StatementEditor } from "./StatementEditor";
import { useViewState } from "../../shared/useViewState";
import { useAddRequest } from "../../shared/useAddRequest";
import { AddButton } from "../../shared/AddButton";
import { StatementRow } from "./StatementParts";
import { GroupHeader } from "../../shared/GroupHeader";
import { TASK_LINKS } from "../tasks/taskLinks";
import { LinkedTasks } from "../tasks/LinkedTasks";
import { useTaskRows } from "../tasks/useTaskRows";

// Vision statements and Fears: grouped by the user's categories, each linked to the goals that serve
// it. `renderGoal(goal, onBack)` draws a goal's page over this one (it lives in App.jsx). A fear's tasks fold under it
// (LinkedTasks; `tasks` are the user's, and the task editor opens through TaskActionsContext).
export function StatementsPage({ kind, uid, tasks, goals, compass, onNav, renderGoal, addRequested, onAddHandled }) {
  const k = STATEMENT_KINDS[kind];
  const link = TASK_LINKS[kind]; // fears have tasks; vision statements don't
  const taskRows = useTaskRows(goals);
  const linkTasks = taskRows.linkTasks(tasks);
  const categories = useMemo(() => compass?.categories || [], [compass]);
  const { items, loading, error, create, update, remove, reorder } = useStatements(uid, kind, goals);
  const [editingId, setEditingId] = useState(null); // null, "new", or a statement's id
  useAddRequest(addRequested, onAddHandled, () => setEditingId("new"));
  const [openGoal, setOpenGoal] = useState(null);
  const [moveError, setMoveError] = useState(false);
  const [view, setView] = useViewState(`nudge-${k.uiKey}-ui-${uid}`);
  const groupSensors = useDragSensors();
  const rowSensors = useDragSensors();

  const groups = useMemo(() => groupStatements(kind, items, categories), [kind, items, categories]);
  const iconFor = useMemo(() => Object.fromEntries(categories.map(c => [c.label, c.icon || ""])), [categories]);
  // The user's group order this session, then any group that is new since.
  const order = useMemo(() => {
    const keys = Object.keys(groups);
    const kept = (view.groupOrder || []).filter(g => keys.includes(g));
    return [...kept, ...keys.filter(g => !kept.includes(g))];
  }, [groups, view.groupOrder]);
  const isOpen = (label) => view.openGroups?.[label] ?? true;
  // When every statement is uncategorized, a lone "General" heading says nothing: show the rows bare.
  const bare = order.length === 1 && order[0] === "General";
  const shownGoals = new Set(view.shownGoals || []);

  // Save and Delete work on the statement as it is now, not as it was when Edit was tapped. If it is
  // deleted elsewhere while open, the editor closes.
  const editing = editingId === "new" ? "new" : items.find(s => s.id === editingId) || null;
  useEffect(() => { if (editingId && editingId !== "new" && !loading && !editing) setEditingId(null); }, [editingId, editing, loading]);
  useEffect(() => { if (openGoal && !goals.some(g => g.id === openGoal.id)) setOpenGoal(null); }, [goals, openGoal]);

  const onGroupDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setView({ groupOrder: arrayMove(order, order.indexOf(active.id), order.indexOf(over.id)) });
  };
  const onRowDragEnd = (label, { active, over }) => {
    if (!over || active.id === over.id) return;
    const list = groups[label];
    const from = list.findIndex(s => s.id === active.id);
    const to = list.findIndex(s => s.id === over.id);
    if (from < 0 || to < 0) return;
    setMoveError(false);
    reorder(arrayMove(list, from, to)).catch(e => { console.error(`Reordering ${kind} failed:`, e); setMoveError(true); });
  };
  const toggleGoals = (id) => setView({ shownGoals: shownGoals.has(id) ? [...shownGoals].filter(x => x !== id) : [...shownGoals, id] });


  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text, position: "relative" }}>
      {editing && (
        <StatementEditor kind={kind} statement={editing === "new" ? null : editing} goals={goals} categories={categories}
          onSave={(fields) => (editing === "new" ? create(fields) : update(editing, fields))}
          onDelete={() => remove(editing)} onClose={() => setEditingId(null)} />
      )}
      {openGoal && (
        <div style={{ position: "absolute", inset: 0, zIndex: 120, background: C.bg, display: "flex", flexDirection: "column" }}>
          {renderGoal(goals.find(g => g.id === openGoal.id) || openGoal, () => setOpenGoal(null))}
        </div>
      )}

      <TopBar title={k.pageTitle} help={HELP[k.uiKey]} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ ...pageColumn, padding: "4px 16px 24px" }}>

          {loading && (
            <div aria-hidden="true" style={{ marginTop: 20, display: "grid", gap: 12 }}>
              {[0, 1, 2].map(i => <div key={i} style={{ height: 52, borderRadius: C.r, background: C.surface2 }} />)}
            </div>
          )}

          {error && (
            <p role="alert" style={{ ...intro(), margin: "16px 4px 0" }}>
              We couldn't load your {k.many}. Check your connection, then open this page again.
            </p>
          )}

          {!loading && !error && items.length === 0 && (
            <div style={{ padding: "12px 4px 0", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
              <p style={intro()}>{k.empty}</p>
              <AddButton onClick={() => setEditingId("new")}>{`Add a ${k.one}`}</AddButton>
            </div>
          )}

          {items.length > 0 && (
            <>
              <p style={{ ...intro(), margin: "8px 4px 16px" }}>{k.intro}</p>
              <span id={`goals-hint-${kind}`} hidden>Shows or hides the goals linked to this {k.one}</span>
              {moveError && (
                <p role="alert" style={{ ...intro(), color: C.red, margin: "0 4px 16px" }}>
                  We couldn't save the new order. Check your connection and drag it again.
                </p>
              )}
              <DndContext sensors={groupSensors} collisionDetection={closestCenter} onDragEnd={onGroupDragEnd}>
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                  {order.map(label => (
                    <Sortable key={label} id={label} style={{ marginBottom: 16 }}>
                      {(drag) => (
                        <div style={{ opacity: drag.isDragging ? 0.6 : 1 }}>
                          {!bare && <GroupHeader label={label} icon={iconFor[label]} countText={statementCount(kind, groups[label].length)}
                            open={isOpen(label)} onToggle={() => setView({ openGroups: { ...view.openGroups, [label]: !isOpen(label) } })}
                            handle={<DragHandle label={label} {...drag} />} />}
                          {(bare || isOpen(label)) && (
                            <div style={{ marginTop: 4, background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden", boxShadow: C.shadow }}>
                              <DndContext sensors={rowSensors} collisionDetection={closestCenter} onDragEnd={(e) => onRowDragEnd(label, e)}>
                                <SortableContext items={groups[label].map(s => s.id)} strategy={verticalListSortingStrategy}>
                                  {groups[label].map((s, i) => (
                                    <Sortable key={s.id} id={s.id} style={{ borderTop: i ? `1px solid ${C.border}` : "none", background: C.surface }}>
                                      {(rowDrag) => (
                                        <div style={{ opacity: rowDrag.isDragging ? 0.85 : 1 }}>
                                          <StatementRow kind={kind} statement={s} goals={goals} open={shownGoals.has(s.id)}
                                            onToggle={() => toggleGoals(s.id)} onEdit={() => setEditingId(s.id)} onOpenGoal={setOpenGoal}
                                            handle={<DragHandle label={s[k.textField]} {...rowDrag} />} />
                                          {link && <LinkedTasks link={link} id={s.id} name={s[k.textField]} linkTasks={linkTasks} style={{ marginTop: -8 }} />}
                                        </div>
                                      )}
                                    </Sortable>
                                  ))}
                                </SortableContext>
                              </DndContext>
                            </div>
                          )}
                        </div>
                      )}
                    </Sortable>
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </div>
      {taskRows.toast}
      <BottomNav active={null} onNav={onNav} />
    </div>
  );
}
